import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Leaf, Plane, ScanSearch, Ticket } from "lucide-react";
import AppDistributionLinks from "../components/AppDistributionLinks";
import AppOwnershipBadge from "../components/AppOwnershipBadge";
import PageMeta from "../components/PageMeta";
import { getAppBySlug } from "../data/site";
import { staggerDelay } from "../lib/stagger";

const HOW_IT_WORKS = [
  {
    title: "Upload your e-ticket",
    description:
      "Start with your flight information so the app can anchor the trip and connect the travel workflow.",
    icon: Ticket,
  },
  {
    title: "Enter baggage weight",
    description:
      "Add the weight you plan to check in so GetLief can reflect lower-emission travel choices tied to baggage load.",
    icon: Plane,
  },
  {
    title: "Upload the baggage tag",
    description:
      "Verify the actual travel-day baggage result with your tag and complete the carbon-aware record for the trip.",
    icon: ScanSearch,
  },
  {
    title: "Earn points for lighter travel",
    description:
      "Travelers accumulate points through the lower-baggage workflow and carbon-neutral participation model described across the live product pages.",
    icon: Leaf,
  },
] as const;

const FEATURE_CARDS = [
  {
    eyebrow: "Carbon",
    title: "Baggage-based carbon calculation",
    description:
      "GetLief centers its climate story on air-passenger baggage and emissions, turning travel data into an understandable carbon-neutral action flow.",
  },
  {
    eyebrow: "Rewards",
    title: "Points for lower-emission habits",
    description:
      "Users are rewarded for participation and lighter travel behavior instead of being shown a passive emissions number with no follow-through.",
  },
  {
    eyebrow: "Travel day",
    title: "Verification through ticket and tag upload",
    description:
      "The workflow uses concrete travel artifacts such as e-tickets and baggage tags, which makes the experience feel operational instead of generic.",
  },
  {
    eyebrow: "Positioning",
    title: "Carbon-neutral companion for flyers",
    description:
      "The live homepage and store listings consistently frame GetLief as an air-passenger carbon-neutral service rather than a broad travel planner.",
  },
] as const;

const DETAIL_NOTES = [
  {
    label: "Official site",
    value: "https://get-lief.com/",
  },
  {
    label: "Store status",
    value: "Live on both the Apple App Store and Google Play.",
  },
  {
    label: "Publisher",
    value: "Google Play listing is published under EcoFlite.",
  },
  {
    label: "Role",
    value: "This site presents GetLief as a contributed project with direct store and homepage access.",
  },
] as const;

export default function GetLiefPage() {
  const app = getAppBySlug("getlief");

  if (!app) {
    return null;
  }

  return (
    <div className="relative flex flex-col gap-14 py-20 md:gap-[4.5rem] md:py-28">
      <PageMeta
        title={app.meta?.title ?? "GetLief | Sherxon"}
        description={app.meta?.description ?? app.description}
        canonicalPath={app.meta?.canonicalPath ?? "/apps/getlief"}
      />

      <div
        className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[720px] -translate-x-1/2 rounded-full opacity-60 blur-[120px]"
        style={{ background: "radial-gradient(circle, oklch(0.74 0.16 145 / 28%) 0%, transparent 70%)" }}
      />
      <div
        className="pointer-events-none absolute top-60 left-[-7rem] h-56 w-56 rounded-full opacity-30 blur-[110px]"
        style={{ background: "radial-gradient(circle, oklch(0.8 0.12 100 / 24%) 0%, transparent 74%)" }}
      />

      <Link
        to="/apps"
        className="stagger-in inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-primary"
        style={{ animationDelay: staggerDelay(0) }}
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All apps
      </Link>

      <section className="grid gap-6 md:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)] md:items-start">
        <div className="flex flex-col gap-6">
          <div
            className="stagger-in flex items-start justify-between gap-3"
            style={{ animationDelay: staggerDelay(1) }}
          >
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border/70 bg-card/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">
              Carbon-neutral travel
            </div>
            <AppOwnershipBadge ownership="contributed" />
          </div>

          <div className="flex flex-col gap-4">
            <div className="stagger-in flex flex-col gap-2" style={{ animationDelay: staggerDelay(2) }}>
              <h1 className="font-display text-5xl leading-[0.94] font-bold tracking-tight sm:text-6xl">
                GetLief turns lighter flying into a carbon-aware reward loop.
              </h1>
            </div>

            <p
              className="stagger-in max-w-2xl text-lg leading-relaxed text-foreground/85 sm:text-xl"
              style={{ animationDelay: staggerDelay(3) }}
            >
              A dedicated mobile experience for travelers who want to measure baggage-linked emissions, verify trips with real travel artifacts, and earn rewards for lower-impact choices.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <p
              className="stagger-in max-w-2xl text-base leading-relaxed text-muted-foreground/80"
              style={{ animationDelay: staggerDelay(4) }}
            >
              The official homepage positions GetLief as an air-passenger carbon-neutral service, while the live store listings describe a practical sequence: upload your e-ticket, enter baggage weight, upload your baggage tag, calculate carbon impact, and collect points.
            </p>
            <p
              className="stagger-in max-w-2xl text-base leading-relaxed text-muted-foreground/80"
              style={{ animationDelay: staggerDelay(5) }}
            >
              This page exists as a clear contributed-project entry inside Sherxon’s portfolio, with direct access to the public website and both store listings.
            </p>
          </div>

          <div
            className="stagger-in flex flex-wrap gap-2.5"
            style={{ animationDelay: staggerDelay(6) }}
          >
            {["App Store live", "Google Play live", "Official website", "Contributed project"].map((signal) => (
              <span
                key={signal}
                className="rounded-full border border-border/80 bg-secondary/35 px-4 py-1.5 text-sm text-foreground/85"
              >
                {signal}
              </span>
            ))}
          </div>
        </div>

        <div
          className="stagger-in relative overflow-hidden rounded-[1.9rem] border border-border/70 bg-card/70 p-6 shadow-[0_18px_80px_-42px_rgba(0,0,0,0.8)] backdrop-blur-xl"
          style={{ animationDelay: staggerDelay(4) }}
        >
          <div
            className="pointer-events-none absolute inset-x-5 top-0 h-28 rounded-b-[2rem] opacity-80 blur-2xl"
            style={{ background: "linear-gradient(180deg, oklch(0.74 0.16 145 / 20%), transparent)" }}
          />
          <div className="relative flex flex-col gap-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
                  Verified links
                </span>
                <h2 className="font-display text-3xl leading-none">{app.name}</h2>
              </div>
              <div className="rounded-full border border-border/80 bg-background/80 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                EcoFlite
              </div>
            </div>

            <p className="text-sm leading-relaxed text-muted-foreground/80">
              Live mobile app presence with direct store routes and an official public website.
            </p>

            <AppDistributionLinks
              appStoreUrl={app.appStoreUrl}
              playStoreUrl={app.playStoreUrl}
              webUrl={app.webUrl}
              staggerStartMs={0}
            />
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-6">
        <div
          className="stagger-in flex items-center justify-between gap-4"
          style={{ animationDelay: staggerDelay(7) }}
        >
          <h2 className="font-display text-3xl sm:text-4xl">How it works</h2>
          <span className="hidden text-xs uppercase tracking-[0.24em] text-muted-foreground sm:block">
            Based on the live product flow
          </span>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {HOW_IT_WORKS.map((step, index) => {
            const Icon = step.icon;
            return (
              <article
                key={step.title}
                className="stagger-in rounded-[1.5rem] border border-border/70 bg-card/55 p-6 backdrop-blur-sm"
                style={{ animationDelay: staggerDelay(8 + index) }}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="rounded-2xl border border-border/70 bg-background/50 p-3">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                      Step {index + 1}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">{step.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground/80">{step.description}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="flex flex-col gap-6">
        <div
          className="stagger-in flex items-center justify-between gap-4"
          style={{ animationDelay: staggerDelay(12) }}
        >
          <h2 className="font-display text-3xl sm:text-4xl">Why the product stands out</h2>
          <span className="hidden text-xs uppercase tracking-[0.24em] text-muted-foreground sm:block">
            Travel, climate, rewards
          </span>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {FEATURE_CARDS.map((feature, index) => (
            <article
              key={feature.title}
              className="stagger-in group relative overflow-hidden rounded-[1.5rem] border border-border/70 bg-card/55 p-6 backdrop-blur-sm transition-transform duration-300 hover:-translate-y-1"
              style={{ animationDelay: staggerDelay(13 + index) }}
            >
              <div
                className="pointer-events-none absolute inset-x-4 top-0 h-20 rounded-b-[1.5rem] opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                style={{ background: "linear-gradient(180deg, oklch(0.74 0.16 145 / 18%), transparent)" }}
              />
              <div className="relative flex flex-col gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
                  {feature.eyebrow}
                </span>
                <h3 className="text-xl font-semibold text-foreground">{feature.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground/80">
                  {feature.description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-[minmax(0,1.08fr)_minmax(18rem,0.92fr)]">
        <div
          className="stagger-in rounded-[1.7rem] border border-border/70 bg-card/60 p-7 backdrop-blur-sm"
          style={{ animationDelay: staggerDelay(17) }}
        >
          <div className="flex flex-col gap-4">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
              Credibility
            </span>
            <h2 className="font-display text-3xl sm:text-4xl">Live product, clear positioning, direct access</h2>
            <p className="text-base leading-relaxed text-muted-foreground/80">
              GetLief already has an active public web presence plus live listings on both major stores. The product message is consistent across those surfaces: carbon-neutral practice for airline passengers, centered on baggage, verification, and rewards.
            </p>
            <div className="flex items-start gap-3 rounded-2xl border border-border/70 bg-background/45 p-4 text-sm leading-relaxed text-muted-foreground/85">
              <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-primary/90" />
              <p>
                This dedicated route exists to represent GetLief as a contributed project inside the portfolio while still linking directly to the official public channels.
              </p>
            </div>
          </div>
        </div>

        <div
          className="stagger-in rounded-[1.7rem] border border-border/70 bg-card/60 p-7 backdrop-blur-sm"
          style={{ animationDelay: staggerDelay(18) }}
        >
          <div className="flex flex-col gap-4">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
              Trust details
            </span>
            <div className="space-y-4">
              {DETAIL_NOTES.map((note) => (
                <div key={note.label} className="border-b border-border/60 pb-4 last:border-b-0 last:pb-0">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    {note.label}
                  </div>
                  <div className="mt-2 text-sm leading-relaxed text-foreground/88">{note.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="stagger-in rounded-[1.8rem] border border-border/70 bg-card/65 p-7 backdrop-blur-xl"
        style={{ animationDelay: staggerDelay(19) }}
      >
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/90">
              Open the product
            </span>
            <h2 className="font-display text-3xl sm:text-4xl">Use the official channels</h2>
            <p className="max-w-2xl text-base leading-relaxed text-muted-foreground/80">
              Download GetLief from the stores or visit the official homepage for the current public product presence.
            </p>
          </div>

          <AppDistributionLinks
            appStoreUrl={app.appStoreUrl}
            playStoreUrl={app.playStoreUrl}
            webUrl={app.webUrl}
            staggerStartMs={0}
          />
        </div>
      </section>
    </div>
  );
}
